import React, { useEffect, useState, useRef } from 'react';
import c from 'classnames';
import styles from './Item.css';
import { VideoPlayer } from './VideoPlayer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ItemKudos } from './ItemKudos';
import { VideoEmbed } from './VideoEmbed';
import { useSelector } from 'react-redux';
import {
  selectIsCardsView,
  selectItemsListState,
  selectShowNsfw,
} from '../../ducks';
import { formatPricing } from '../../lib/helpers';
import { ListType } from '../../apis/dumpert';
import { ITEMMEDIA } from '../../lib/labels';
import { settings } from '../../settings';
import { ButtonSwitchNSFW } from '../settingsoptions';
import { adBlockerActive } from '../../adblkcheckerscript';

const formatDuration = (durationTime, numOfMedia, mediatype) => {
  if (typeof durationTime === 'number' && mediatype !== 'FOTO') {
    const date = new Date(null);
    date.setSeconds(durationTime);
    return date.toISOString().substr(14, 5);
  }
  return numOfMedia > 1 ? numOfMedia : '';
};

export const getSortedRelatedItems = items => {
  try {
    const viewed = window.sessionStorage.getItem('viewedItems' || '').split(',');
    return items.sort((x, y) => !viewed.some(z => z === y.id) ? 1 : !viewed.some(z => z === x.id) ? -1 : -1);
  } catch (e) {
    return items;
  }
};

export const MediaItem = ({ item, isToppers, isSidebar, counter, highlight, isStoreItem }) => {
  const isCardsView = useSelector(selectIsCardsView);
  const showNsfw = useSelector(selectShowNsfw);
  const [media] = item.media || [];
  const { mediatype = 'VIDEO', duration = 0 } = media || {};
  const placeholder = '/favicon-152.png';
  const stills = item.stills || {};
  const { kudos_total, kudos_today } = item.stats;
  const canvasRef = useRef();
  const { current } = canvasRef;
  const thumbImage = stills[!isCardsView || isSidebar ? 'thumb' : 'still-medium'] || placeholder;

  if(item.media === undefined) {
    item.media = [];
  }

  useEffect(() => {
    if (current) {
      const img = new Image();
      img.onload = () => {
        const { width, height } = img;
        const pixelValue = isCardsView ? 40 : 15;
        const multiply = isCardsView ? 2.25 : 1;

        current.width = width * multiply;
        current.height = height * multiply; 
        const ctx = current.getContext('2d');
      
        const fw = (width / pixelValue) | 0;
        const fh = (height / pixelValue) | 0;

        ctx.imageSmoothingEnabled = false;
        ctx.mozImageSmoothingEnabled = false;
        ctx.msImageSmoothingEnabled = false;
        ctx.webkitImageSmoothingEnabled = false;

        ctx.drawImage(img, 0, 0, fw * multiply, fh * multiply);
        ctx.drawImage(current, 0, 0, fw * multiply, fh * multiply, 0, 0, width * multiply, height * multiply);
      };
      img.src = thumbImage;
    }
  }, [current, showNsfw, isCardsView]);

  return (
    <div className={c(styles.media_container)}> 
      <div
        className={c(
          styles.media,
          { [styles.topper]: isToppers },
        )}
        data-toppers-number={counter}
      >
        {highlight && <div className={styles.highlighted_item}>
          <FontAwesomeIcon icon={['fas', 'check']} size="lg"/>
        </div>}
        {item.nsfw
          ? <canvas ref={canvasRef} />
          : <img
            src={thumbImage}
            alt={item.title}
            className={c({ [styles.store_image]: isStoreItem } )}
          />
        }
        <div className={c(styles.caption, 'caption')}>
          {isStoreItem && (
            <span>{formatPricing(item.store_data.price)}</span>
          )}
          {!isStoreItem && (
            <>
              <span>
                <FontAwesomeIcon icon={['fas', mediatype === 'VIDEO' ? 'film' : 'image']} size="lg"/>
                {formatDuration(duration, item.media.length, mediatype)}
              </span>
              <ItemKudos numOfKudos={isSidebar && isToppers ? kudos_today : kudos_total}/>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const MediaItemDetails = ({ item, variants, mediatype, duration, counter, index, isLoggedIn }) => {
  const showNsfw = useSelector(selectShowNsfw);
  const related = useSelector(state => selectItemsListState(state, ListType.related));
  const [{ version, uri }] = variants;
  const isVideo = mediatype === 'VIDEO';
  const isAudio = mediatype === 'AUDIO';
  const isEmbed = isVideo && version === 'embed';
  const isNSFW = !item.nsfw || item.nsfw && showNsfw;
  const [noads, setViewCount] = useState(false);
  const embedType = uri.split(':')[0];
  const kijkNLEmbedWithoutAds = adBlockerActive(true) && embedType === 'kijk.nl' && isLoggedIn;
  useEffect(() => {
    if(item.nopreroll && isVideo) {
      setViewCount(true);
    } else if(item.nsfw && isVideo) {
      setViewCount(true);
    } else if(!item.nsfw && !item.nopreroll && isVideo && !isEmbed) {
      const viewCount = window.sessionStorage.getItem('viewcount') || 1;
      setViewCount(parseInt(viewCount) % settings.PREROLL_TIME !== 0);
      window.sessionStorage.setItem('viewcount', parseInt(viewCount) + 1);
    }
  }, []);

  return (
    <>
      {/* EXAMPLES MULTIPLE MEDIA ITEMS
      MULTIPLE IMAGES: http://localhost:8080/item/7683953_0af3f9f4
      MULTIPLE VIDEOS: (check stills?)
        http://localhost:8080/item/7677657_d42ed649
        http://localhost:8080/item/7685297_1d746e4f
      MULTIPLE MIXED ITEMS:
        http://localhost:8080/item/7682385_d35af77f
      YOUTUBE:
        http://localhost:8080/item/7690059_64c58a33
      TWITTER:
        http://localhost:8080/item/7254093_5872e637
        http://localhost:8080/item/7677875_5f7732bb
      AUDIO:
        http://localhost:8080/item/2149551_1d05cfc7

      IMAGE NOT SHOWING : http://localhost:8080/item/6957017_4eaa8126
      VERSION NOT PROVIDED : http://localhost:8080/item/7675795_1f7bc9df
    */}  
      <div className={c(
        styles.media_container,
        { [styles.nsfw]: item.nsfw && !showNsfw },
      )}>
        <div className={c(
          styles.media_item,
          { 
            [styles.twitter_embed]: embedType === 'twitter',
            [styles['no-ratio']]: isEmbed && (
              embedType !== 'kijk.nl' && embedType !== 'youtube' && embedType !== 'twitch'
            ),
            [styles['fixed-height']]: embedType === 'facebook',
            
          },
        )}>
          <div
            className={c(
              styles.media,
              { 
                [styles.medianumber]: counter,
                [styles.audio]: isAudio,
                [styles['padding-top']]: embedType === 'instagram', 
              },
            )}
            data-media-number={`${index + 1}`}
          >
            {!isNSFW && (
              <>
                <h2>{ITEMMEDIA.NSFW_TEXT}</h2>
                <hr />
                <ButtonSwitchNSFW />
              </>
            )}

            {(isEmbed && !kijkNLEmbedWithoutAds) && <VideoEmbed uri={uri}/>}

            {isVideo && (version !== 'embed' || kijkNLEmbedWithoutAds) && isNSFW &&
              (related.isLoaded || related.error) && (
              <VideoPlayer variants={variants} related={getSortedRelatedItems(related.items)}
                itemId={item.id} index={index} title={item.title} noads={noads}
                still={item.still ? item.still : '/favicon-152.png'} duration={duration}
                dateCreated={item.date} tags={item.tags} description={item.description}/>
            )}
            {mediatype === 'FOTO' && isNSFW && <a
              href={variants[0].uri}
              target="_blank"
              rel="noopener noreferrer"
              title="Opklik voor groot">
              <img src={variants[0].uri} alt={item.title}/>
            </a>
            }
            {isAudio && (
              <audio controls autoPlay>
                <source src={variants[0].uri} type="audio/mpeg"/>
                  Your browser does not support the audio element.
              </audio>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
